import React from 'react';
import type {OpenChangeReason} from '@floating-ui/react';

/**
 * Used to coordinate Modal children
 */
import {RegisterModalWithOverlay} from '@packages/systems/ui/overlay';
import {useFloatingDialog} from '../useFloatingDialog';

import type {UseDialog, UseDialogOptions} from '../useFloatingDialog';

export type DialogChangeHandler = (
  event: Event | undefined,
  meta: {reason?: OpenChangeReason}
) => void;

export type ModalContextProps = {
  /**
   * This is used to prevent the Modal from moving when the header is anchored.
   */
  shouldAnchorModalTop?: boolean;
  /**
   * CloseButton render states.
   */
  hasCloseButton?: boolean;
  /**
   * Footer render states.
   */
  hasFooter?: boolean;
  /**
   * Header render states.
   */
  hasHeader?: boolean;
  /**
   * Determines if Modal should be handled like a non-modal Dialog. Non-modal
   * Dialogs do not content underneath the Modal.
   */
  isNonModalDialog?: boolean;
  /**
   * The unique ID of the Modal description.
   */
  modalDescriptionId?: string;
  /**
   * Internal setter for modal description id.
   */
  setModalDescriptionId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  /**
   * The unique ID of the Modal title.
   */
  modalTitleId?: string;
  /**
   * Internal setter for modal title ids.
   */
  setModalTitleId: React.Dispatch<React.SetStateAction<string | undefined>>;
  /**
   * The ref of an element to receive focus when the Modal closes.
   * Otherwise, the Modal will default to the last active element before it
   * opened.
   */
  onCloseFocusRef?: React.MutableRefObject<HTMLElement | null>;
  /**
   * The ref of an element to receive focus when the Modal opens.
   * Otherwise, the Modal will default to the first focusable element.
   */
  onOpenFocusRef?: React.MutableRefObject<HTMLElement | null>;
  /**
   * Internal close callback for the Modal.
   */
  onModalClose: DialogChangeHandler;
  /**
   * An internal method to (very simply) register the ModalCloseButton.
   */
  setHasRenderedCloseButton: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * An internal method to (very simply) register the ModalHFooter.
   */
  setHasRenderedFooter: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * An internal method to (very simply) register the ModalHeader.
   */
  setHasRenderedHeader: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Determines if the Modal should hide if the backdrop is clicked.
   */
  shouldHideOnClickOutside?: boolean;
  /**
   * If the content within the Modal overflows beyond the viewport, you can use
   * the scrollBehavior to control how scrolling should happen.
   *
   * - Inside: Scrolling only occurs within the Modal body.
   * - Outside: Scrolling within the viewport.
   */
  scrollBehavior: 'inside' | 'outside';
  /**
   * The useDialog() state from @floating-ui
   */
  dialog: UseDialog;
  __forceMount?: boolean;
};

export const ModalContext = React.createContext<ModalContextProps | null>(null);

export const useModal = () => {
  const context = React.useContext(ModalContext);

  if (context == null) {
    throw new Error('Modal components must be wrapped in <ModalProvider />');
  }

  return context;
};

/**
 * A helper to encapsulate React state for dialogs.
 */
export const useDialogState = (id?: string) => {
  const [hasCloseButton, setHasRenderedCloseButton] = React.useState(false);
  const [hasFooter, setHasRenderedFooter] = React.useState(false);
  const [hasHeader, setHasRenderedHeader] = React.useState(false);
  const [modalTitleId, setModalTitleId] = React.useState<string | undefined>(
    id ? `${id}-title` : undefined
  );
  const [modalDescriptionId, setModalDescriptionId] = React.useState<
    string | undefined
  >(id ? `${id}-description` : undefined);

  return {
    hasCloseButton,
    setHasRenderedCloseButton,
    hasFooter,
    setHasRenderedFooter,
    hasHeader,
    setHasRenderedHeader,
    modalTitleId,
    setModalTitleId,
    modalDescriptionId,
    setModalDescriptionId,
  };
};

export type ModalProviderProps = {
  /**
   * Renders the contents.
   */
  children?: React.ReactNode;
  /**
   * Automatically renders the close button.
   */
  hasCloseButton?: boolean;
  /**
   * Determines if Modal should be handled like a non-modal Dialog. Non-modal
   * Dialogs do not apply a backdrop, prevent body scrolling, or close on outside click.
   */
  isNonModalDialog?: boolean;
  /**
   * Controlled open state.
   */
  isOpen?: boolean;
  /**
   * Callback when the Modal closes.
   */
  onClose?: DialogChangeHandler;
  /**
   * Callback when the Modal close animation completes.
   */
  onCloseComplete?: DialogChangeHandler;
  /**
   * The ref of an element to receive focus when the Modal closes.
   * Otherwise, the Modal will default to the last active element before it
   * opened.
   */
  onCloseFocusRef?: React.MutableRefObject<HTMLElement | null>;
  /**
   * Callback when the `Modal` opens. Unlike `onClose`, this callback isn't needed to
   * coordinate `isOpen` state.
   */
  onOpen?: (event?: Event) => void;
  /**
   * The ref of an element to receive focus when the Modal opens.
   * Otherwise, the Modal will default to the first focusable element.
   */
  onOpenFocusRef?: React.MutableRefObject<HTMLElement | null>;
  /**
   * When true, the modal fills the viewport. Forces scroll behavior to "inside"
   * so the modal body becomes the scroll container.
   */
  isFullScreen?: boolean;
  /**
   * If the content within the Modal overflows beyond the viewport, you can use
   * the scrollBehavior to control how scrolling should happen.
   *
   * Inside: Scrolling only occurs within the Modal body.
   * Outside: Scrolling within the viewport.
   */
  scrollBehavior?: 'inside' | 'outside';
  /**
   * This is used to prevent the Modal from moving when the header is anchored.
   */
  shouldAnchorModalTop?: boolean;
  /**
   * Determines if the Modal should run through its animations on mount and unmount. By default, animations
   * respect the user's reduced motion settings. This prop allows for the disabiling of animations regardless
   * of user settings.
   */
  shouldDisableAnimations?: boolean;
  /**
   * Determines if the Modal should hide if the backdrop is clicked.
   */
  shouldHideOnClickOutside?: boolean;
  /**
   * Determines if the Modal should hide when the Escape key is pressed.
   * Defaults to `true`.
   */
  shouldHideOnEsc?: boolean;
  /**
   * Adding this to force the Modal contents to mount on the initial render.
   * This is to prevent a bug where AddCreditCardModal is running a useEffect based on another component's state update while not having the expected parameters.
   *
   * @TODO - we should refactor AddCreditCardModal and remove this prop.
   */
  __forceMount?: boolean;
};

export const ModalProvider = ({
  children,
  isFullScreen,
  isNonModalDialog,
  isOpen,
  onClose,
  hasCloseButton,
  onCloseComplete,
  onCloseFocusRef,
  onOpen,
  onOpenFocusRef,
  scrollBehavior = 'inside',
  shouldDisableAnimations,
  shouldHideOnClickOutside,
  shouldHideOnEsc,
  shouldAnchorModalTop,
  __forceMount,
}: ModalProviderProps) => {
  const modalState = useDialogState();

  const effectiveScrollBehavior = isFullScreen
    ? ('inside' as const)
    : scrollBehavior;

  React.useEffect(() => {
    if (isOpen) {
      onOpen?.();
    }
  }, [isOpen, onOpen]);

  const onOpenChange = React.useCallback<
    NonNullable<UseDialogOptions['onOpenChange']>
  >(
    (change, event, reason) => {
      if (change) {
        onOpen?.(event);
      } else {
        onClose?.(event, {reason});
        onCloseComplete?.(event, {reason});
      }
    },
    [onClose, onCloseComplete, onOpen]
  );

  const dialog = useFloatingDialog({
    open: isOpen,
    onOpenChange,
    shouldHideOnClickOutside,
    shouldHideOnEsc,
    isNonModalDialog,
  });

  const contextHasCloseButton = hasCloseButton || modalState.hasCloseButton;

  const onModalClose: DialogChangeHandler = React.useCallback(
    (event, meta) => {
      const {reason} = meta;
      onOpenChange(false, event, reason);
    },
    [onOpenChange]
  );

  const contextValue = {
    ...modalState,
    hasCloseButton: contextHasCloseButton,
    scrollBehavior: effectiveScrollBehavior,
    onModalClose,
    onCloseFocusRef,
    onOpenFocusRef,
    dialog,
    isNonModalDialog,
    shouldHideOnClickOutside,
    shouldAnchorModalTop,
  };

  return (
    <RegisterModalWithOverlay>
      <ModalContext.Provider value={contextValue}>
        {shouldDisableAnimations && !isOpen ? null : children}
      </ModalContext.Provider>
    </RegisterModalWithOverlay>
  );
};

ModalProvider.displayName = 'ModalProvider';
